import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseSuspenseInfiniteQueryResult,
} from "@tanstack/react-query"
import { useInViewport } from "@mantine/hooks"
import { Pagination } from "../../utils/Pagination"
import { DefaultList, DefaultListProps } from "../default-list"
import { Loader } from "@mantine/core"

export interface DefaultInfiniteListProps<
  TSelectable extends boolean = false,
  TQueryResult extends
    | UseSuspenseInfiniteQueryResult<InfiniteData<Pagination<any>>>
    | UseInfiniteQueryResult<InfiniteData<Pagination<any>>> =
    | UseSuspenseInfiniteQueryResult<InfiniteData<Pagination<any>>>
    | UseInfiniteQueryResult<InfiniteData<Pagination<any>>>,
> extends Omit<
    DefaultListProps<
      TQueryResult extends UseSuspenseInfiniteQueryResult<
        InfiniteData<Pagination<infer U>>
      >
        ? U
        : never,
      TSelectable
    >,
    "data"
  > {
  queryResult: TQueryResult
}

export function DefaultInfiniteList<
  TSelectable extends boolean = false,
  TQueryResult extends
    | UseSuspenseInfiniteQueryResult<InfiniteData<Pagination<any>>>
    | UseInfiniteQueryResult<InfiniteData<Pagination<any>>> =
    | UseSuspenseInfiniteQueryResult<InfiniteData<Pagination<any>>>
    | UseInfiniteQueryResult<InfiniteData<Pagination<any>>>,
>({
  queryResult,
  ...props
}: DefaultInfiniteListProps<TSelectable, TQueryResult>) {
  const { data, isLoading, isError, fetchNextPage, hasNextPage } = queryResult

  const { ref, inViewport } = useInViewport()

  React.useEffect(() => {
    if (inViewport && hasNextPage && !isLoading && !isError) {
      fetchNextPage()
    }
  }, [inViewport, fetchNextPage])

  const sentinel = React.useMemo(() => {
    if (!hasNextPage || isError) {
      return null
    }

    if (isLoading) {
      return <Loader />
    }

    return (
      <div
        ref={ref}
        style={{
          height: "100px",
          width: "100%",
          opacity: 0,
          flexShrink: 0,
        }}
      />
    )
  }, [ref, hasNextPage, isLoading, isError])

  if (!data || !Array.isArray(data.pages)) {
    return null
  }

  // Fallback: leere Seite oder page.data undefiniert
  const items = data.pages.flatMap((page) =>
    Array.isArray(page?.data) ? page.data : []
  )

  return (
    <DefaultList {...props} data={items}>
      {sentinel}
    </DefaultList>
  )
}
